import { Task } from '../types/task';
import { TimeGroupKey } from '../components/v0/tasks/TimeGroup';

/**
 * 根据任务的开始时间或截止日期将任务分组，优先使用 startAt
 */
export const groupTasksByTime = (tasks: Task[]): Record<TimeGroupKey, Task[]> => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // 设置为今天的开始时间
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  // 初始化时间分组
  const groups: Record<TimeGroupKey, Task[]> = {
    noDate: [],    // 无日期
    future: [],    // 未来（比明天更远）
    tomorrow: [],  // 明天
    today: [],     // 今天
    yesterday: [], // 昨天
    past: []       // 过去（比昨天更早）
  };
  
  // 按时间分组任务
  tasks.forEach(task => {
    // 优先使用 startAt，如果没有则使用 dueAt
    const timeStr = task.startAt || task.dueAt;
    
    if (!timeStr) {
      groups.noDate.push(task);
      return;
    }
    
    const date = new Date(timeStr);
    date.setHours(0, 0, 0, 0); // 忽略时间部分，只比较日期
    
    if (date.getTime() === today.getTime()) {
      groups.today.push(task);
    } else if (date.getTime() === tomorrow.getTime()) {
      groups.tomorrow.push(task);
    } else if (date.getTime() === yesterday.getTime()) {
      groups.yesterday.push(task);
    } else if (date.getTime() > tomorrow.getTime()) {
      groups.future.push(task);
    } else {
      groups.past.push(task);
    }
  });
  
  // 在每个分组内对任务进行排序
  const sortTasks = (a: Task, b: Task) => {
    // 先按完成状态排序，未完成的在前
    if (a.done !== b.done) return a.done ? 1 : -1;
    
    // 获取任务的时间（优先使用 startAt）
    const getTaskTime = (task: Task) => {
      if (task.startAt) return new Date(task.startAt).getTime();
      if (task.dueAt) return new Date(task.dueAt).getTime();
      return 0;
    };
    
    const timeA = getTaskTime(a);
    const timeB = getTaskTime(b);
    
    if (timeA === 0 && timeB === 0) return a.title.localeCompare(b.title);
    if (timeA === 0) return 1;
    if (timeB === 0) return -1;
    
    // 按时间升序
    return timeA - timeB;
  };
  
  // 对每个分组应用排序
  Object.values(groups).forEach(group => {
    group.sort(sortTasks);
  });
  
  return groups;
};

/**
 * 根据任务的项目ID将任务分组
 */
export const groupTasksByProject = (tasks: Task[]): Record<string, Task[]> => {
  const result: Record<string, Task[]> = {};
  
  tasks.forEach(task => {
    const projectId = task.projectId || 'none';
    if (!result[projectId]) {
      result[projectId] = [];
    }
    result[projectId].push(task);
  });
  
  // 对每个项目内的任务排序
  Object.keys(result).forEach(projectId => {
    result[projectId].sort((a, b) => {
      // 先按完成状态排序，未完成的在前
      if (a.done !== b.done) return a.done ? 1 : -1;
      
      // 然后按截止日期排序（有日期的排在前面）
      if (a.dueAt && !b.dueAt) return -1;
      if (!a.dueAt && b.dueAt) return 1;
      
      if (a.dueAt && b.dueAt) {
        return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
      }
      
      // 最后按标题排序
      return a.title.localeCompare(b.title);
    });
  });
  
  return result;
}; 